/**
 * React-compatible Pinia Options API facade backed by per-property Jotai atoms.
 *
 * Port of createJotaiPiniaFacadeForVue — identical store semantics
 * (state atom map, getters, actions, `this`-bound proxy) but uses
 * useAtomValue for React re-renders instead of Vue's reactive().
 *
 * The returned factory function is a React hook. Call it once at the
 * top level of each component:
 *
 *   const store = useDtfStore();
 *   // then use store.someState, store.someGetter, store.someAction(...)
 *
 * Do NOT call it inside nested functions — it follows React rules of hooks.
 */

import { atom } from "jotai/vanilla";
import { useAtomValue } from "jotai/react";
// @ts-ignore TS(7016)
import isEqual from "lodash.isequal";
import { setAutoFreeze } from "immer";

type WritableAtom<T = unknown> = { init: T };
type AtomMap = Record<string, WritableAtom>;

type JotaiStore = {
  get(a: WritableAtom | { read: unknown }): unknown;
  set<T>(a: WritableAtom<T>, value: T): void;
  sub(a: WritableAtom | { read: unknown }, callback: () => void): () => void;
};

type StoreProxy = Record<string, unknown>;

type FacadeOptions = {
  jotaiStore: JotaiStore;
  state: AtomMap;
  getters?: Record<string, (this: StoreProxy) => unknown>;
  actions?: Record<string, (this: StoreProxy, ...args: unknown[]) => unknown>;
};

export function createJotaiPiniaFacadeForReact({
  jotaiStore,
  state: rootAtoms,
  getters: getterDefs = {},
  actions: actionDefs = {}
}: FacadeOptions): () => StoreProxy {
  setAutoFreeze(false);

  // Storage for keys written via set() that are not declared in rootAtoms.
  const undeclaredState: StoreProxy = {};

  // ── combined atom for React subscriptions ────────────────────────────
  // Tracks all root atoms. Uses a version counter with deep equality to
  // avoid spurious re-renders when immer produces new references for
  // logically identical data.

  const allAtoms = Object.values(rootAtoms) as WritableAtom[];
  let $combinedVersion = 0;
  const $prevAtomValues = new Map<WritableAtom, unknown>();

  const $combined = atom((get: (a: unknown) => unknown) => {
    let anyChanged = false;
    for (const a of allAtoms) {
      const next = get(a);
      if (!$prevAtomValues.has(a) || $prevAtomValues.get(a) !== next) {
        anyChanged = true;
        $prevAtomValues.set(a, next);
      }
    }
    if (anyChanged) $combinedVersion++;
    return $combinedVersion;
  });

  // ── store proxy ───────────────────────────────────────────────────────

  // eslint-disable-next-line prefer-const
  let store: StoreProxy = {} as StoreProxy;

  store = new Proxy({} as StoreProxy, {
    get(_target, key: string) {
      if (key === "$reset") {
        return () => {
          for (const [, a] of Object.entries(rootAtoms)) {
            jotaiStore.set(a as WritableAtom, a.init);
          }
        };
      }

      if (key in actionDefs) {
        return (...args: unknown[]) => actionDefs[key]!.call(store, ...args);
      }

      if (key in getterDefs) {
        return getterDefs[key]!.call(store);
      }

      if (key in rootAtoms) {
        return jotaiStore.get(rootAtoms[key] as WritableAtom);
      }

      return undeclaredState[key];
    },

    set(_target, key: string, value: unknown) {
      if (key in rootAtoms) {
        // Jotai treats function values as updater callbacks (calling fn(prevValue)).
        // Wrap functions in a thunk so Jotai stores the function itself, matching
        // Pinia/Vue behaviour where state assignments are always stored as-is.
        const atomValue = typeof value === "function" ? () => value : value;
        jotaiStore.set(rootAtoms[key] as WritableAtom, atomValue as never);
      } else {
        undeclaredState[key] = value;
      }
      return true;
    }
  });

  // ── factory (React hook) ──────────────────────────────────────────────

  function useStore(): StoreProxy {
    useAtomValue($combined, { store: jotaiStore as never });
    return store;
  }

  // Expose the raw proxy for non-React contexts (init functions, action cross-calls, etc.)
  useStore.store = store;

  return useStore;
}
